import { RefObject } from 'react';
import { BenchmarkScoreRange } from '../../shared/types/widget';
import { SelectedBenchmark } from '../../shared/constants/filter/filter';
import {
  ConfigDataType,
  IBenchmarkType,
  DimensionKey,
} from '../../shared/types/filters';
import { COLORS } from '../../shared/constants/colors';
import { ChartScoreRangeIndex } from '../../shared/constants/chart';
import { PageName } from '../../shared/constants/app';

interface BenchmarkDetail {
  value: number;
  color: string;
  label: string;
}

interface AllBenchmarks {
  thriving: BenchmarkDetail;
  attention: BenchmarkDetail;
  global: BenchmarkDetail;
}

export function replaceSpacesWithUnderscores(
  sentence: string,
  concateWord?: string,
  mobileSheet?: boolean,
) {
  let concatString = sentence.split(' ').join('_');
  if (mobileSheet) {
    concatString += '_ismobile';
  } else {
    concatString += '_' + concateWord;
  }
  return concatString;
}

export const isRelativeUrl = (url: string): boolean => {
  return !url.startsWith('http://') && !url.startsWith('https://');
};

export function isElementScrollable(div: HTMLElement): {
  isScrollable: boolean;
  isScrollableX: boolean;
  isScrollableY: boolean;
} {
  let isScrollable = false;
  let isScrollableX = div.scrollWidth > div.clientWidth;
  let isScrollableY = div.scrollHeight > div.clientHeight;

  if (isScrollableX || isScrollableY) isScrollable = true;
  if (isScrollableX) isScrollableX = true;
  if (isScrollableY) isScrollableY = true;

  // If neither X nor Y is scrollable, check nested elements
  if (!isScrollableX || !isScrollableY) {
    Array.from(div.querySelectorAll('*')).some((el) => {
      const isScrollableXNested = el.scrollWidth > el.clientWidth;
      const isScrollableYNested = el.scrollHeight > el.clientHeight;

      // If any nested element is scrollable, update and stop further checks
      if (isScrollableXNested || isScrollableYNested) {
        isScrollableX = isScrollableX || isScrollableXNested;
        isScrollableY = isScrollableY || isScrollableYNested;
        isScrollable = true;
        return true;
      }
      return false;
    });
  }

  return {
    isScrollable: isScrollable,
    isScrollableX: isScrollableX,
    isScrollableY: isScrollableY,
  };
}

// Find label and color from range based on value passed
export function getLabelAndColor(
  value: number,
  ranges: BenchmarkScoreRange[],
): { label: string; color: string } | null {
  for (const range of ranges) {
    if (value >= range.min && value <= range.max) {
      return { label: range.label, color: range.color };
    }
  }
  return null;
}

// Get label, color and cutoff of all benchmarks based on meta
export function getAllBenchmarkDetails(
  meta: any,
  benchmarkType: IBenchmarkType,
  configData: ConfigDataType,
): AllBenchmarks {
  const scoreRanges = meta?.scoreRanges ?? [];
  const dimension = meta?.dimension;
  const slug = window.location.pathname.split('/d/')[1] ?? '';

  const isBurnoutPage = slug?.includes(PageName.BURNOUT);

  const thrivingValue = isBurnoutPage
    ? (scoreRanges?.[ChartScoreRangeIndex.THRIVING_INDEX]?.max ?? 0)
    : (scoreRanges?.[ChartScoreRangeIndex.THRIVING_INDEX]?.min ?? 0);

  const attentionValue = isBurnoutPage
    ? (scoreRanges?.[ChartScoreRangeIndex.ATTENTION_INDEX]?.min ?? 0)
    : (scoreRanges?.[ChartScoreRangeIndex.ATTENTION_INDEX]?.max ?? 0);

  const globalValue = Math.round(
    benchmarkType === SelectedBenchmark.GLOBAL
      ? configData?.industries[SelectedBenchmark.GLOBAL]?.meta?.average?.[
          dimension as DimensionKey
        ]
      : configData?.industries?.[benchmarkType - 1]?.meta?.average?.[
          dimension as DimensionKey
        ],
  );

  const thriving = {
    value: thrivingValue,
    ...getLabelAndColor(thrivingValue, scoreRanges)!,
  };

  const attention = {
    value: attentionValue,
    ...getLabelAndColor(attentionValue, scoreRanges)!,
  };

  const global = {
    value: globalValue,
    label: '',
    color: '#696969',
  };

  return {
    thriving,
    attention,
    global,
  };
}

export const setRefAtIndex =
  <T>(refArray: RefObject<(T | null)[]>, index: number) =>
  (el: T | null) => {
    refArray.current[index] = el;
  };

export const isNumber = (value: string): boolean => {
  return /^-?\d+(\.\d+)?$/.test(value);
};
